import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Circle,
  CircleMarker,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  ZoomControl,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type {
  BasemapId,
  LiveEmployee,
  MapSettings,
  PointDelivery,
  RouteDto,
} from '@geotracker/contracts';
import { cn } from '@/lib/utils';
import {
  formatAge,
  formatBearing,
  formatClock,
  formatCoordinates,
  formatDistance,
  formatDuration,
  formatLag,
  formatSpeed,
} from '@/lib/format';
import { Basemap, availableBasemaps, basemapById } from './basemaps';

/**
 * Leaflet's default marker images resolve to broken URLs under a bundler, so
 * every marker here is a div icon. That turns out to be the better answer
 * anyway: an employee's icon has to encode direction, and a PNG cannot rotate.
 */

const STALE_SECONDS = 300;

type Tone = 'moving' | 'stationary' | 'stale' | 'mock';

const TONE_CSS: Record<Tone, string> = {
  moving: 'hsl(var(--track-moving))',
  stationary: 'hsl(var(--track-stopped))',
  stale: 'hsl(var(--warning))',
  mock: 'hsl(var(--danger))',
};

/** How the trail is coloured. See index.css — two axes, one at a time. */
export type ColourBy = 'movement' | 'delivery';

export const DELIVERY_CSS: Record<PointDelivery, string> = {
  live: 'hsl(var(--track-live))',
  delayed: 'hsl(var(--track-delayed))',
  backfill: 'hsl(var(--track-backfill))',
};

export const DELIVERY_LABEL: Record<PointDelivery, string> = {
  live: 'Live',
  delayed: 'Delayed',
  backfill: 'Backfilled',
};

/**
 * Width of the translucent casing drawn under a run, per class.
 *
 * The redundant encoding that makes the delivery ramp readable without colour:
 * a backfilled stretch is visibly fatter than a live one at any zoom, in
 * greyscale, and to a viewer who cannot separate the amber from the crimson.
 */
const DELIVERY_CASING: Record<PointDelivery, number> = {
  live: 0,
  delayed: 9,
  backfill: 14,
};

function toneOf(employee: LiveEmployee): Tone {
  if (employee.state.isMock) return 'mock';
  if ((employee.position?.ageSeconds ?? Infinity) > STALE_SECONDS) return 'stale';
  return employee.movement.state === 'moving' ? 'moving' : 'stationary';
}

/**
 * A moving employee gets an arrow pointing where they are going; everyone else
 * gets a dot.
 *
 * The shape is the point. Direction encoded only as a colour or a popup line is
 * direction nobody reads — on a map, "which way is this van heading" should be
 * answerable without clicking. When the server has no course (they are standing
 * still, or have not moved far enough to have one) the marker is deliberately a
 * plain dot rather than an arrow left pointing at the last known heading, which
 * would be a stale claim rendered as a fact.
 */
function employeeIcon(employee: LiveEmployee, selected: boolean): L.DivIcon {
  const tone = toneOf(employee);
  const color = TONE_CSS[tone];
  const bearing = employee.movement.bearing;
  const ring = selected
    ? 'box-shadow:0 0 0 3px hsl(var(--background)),0 0 0 5px ' + color + ';'
    : 'box-shadow:0 0 0 2px hsl(var(--background));';

  if (bearing !== null && tone === 'moving') {
    return L.divIcon({
      className: '',
      html: `<div style="transform:rotate(${bearing}deg);width:26px;height:26px;display:grid;place-items:center">
               <svg viewBox="0 0 24 24" width="26" height="26" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.45))">
                 <path d="M12 2 L19 21 L12 16.5 L5 21 Z" fill="${color}"
                       stroke="hsl(var(--background))" stroke-width="1.6" stroke-linejoin="round"/>
               </svg>
             </div>`,
      iconSize: [26, 26],
      iconAnchor: [13, 13],
    });
  }

  const size = selected ? 16 : 13;
  return L.divIcon({
    className: '',
    html: `<div style="background:${color};width:${size}px;height:${size}px;border-radius:50%;${ring}"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/** Numbered pin for a stop, so the map and the stops list refer to the same thing. */
function stopIcon(index: number, long: boolean): L.DivIcon {
  const color = long ? 'hsl(var(--track-stopped))' : 'hsl(var(--track-idle))';
  return L.divIcon({
    className: '',
    html: `<div style="background:${color};color:white;width:22px;height:22px;border-radius:50%;
             display:grid;place-items:center;font:600 11px/1 ui-sans-serif,system-ui;
             box-shadow:0 0 0 2px hsl(var(--background)),0 1px 3px rgba(0,0,0,.4)">${index + 1}</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

/** Inclusive index range into `route.points`, used to frame a run. */
export interface IndexRange {
  fromIndex: number;
  toIndex: number;
}

interface Props {
  employees: LiveEmployee[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  route: RouteDto | null;
  /** Index into `route.points` for the replay marker, when scrubbing. */
  replayIndex: number | null;
  focusStop: number | null;
  /** A stretch of trail to frame — set when an upload episode is clicked. */
  focusRange: IndexRange | null;
  settings: MapSettings;
  basemap: BasemapId;
  onBasemapChange: (id: BasemapId) => void;
  colourBy: ColourBy;
  onColourByChange: (mode: ColourBy) => void;
  /** Delivery classes currently drawn. Empty is treated as "all". */
  deliveryFilter: ReadonlySet<PointDelivery>;
  onDeliveryFilterChange: (next: ReadonlySet<PointDelivery>) => void;
  className?: string;
}

export function MapView({
  employees,
  selectedId,
  onSelect,
  route,
  replayIndex,
  focusStop,
  focusRange,
  settings,
  basemap,
  onBasemapChange,
  colourBy,
  onColourByChange,
  deliveryFilter,
  onDeliveryFilterChange,
  className,
}: Props) {
  const positioned = useMemo(() => employees.filter((e) => e.position), [employees]);
  const [googleError, setGoogleError] = useState<string | null>(null);

  const definition = basemapById(basemap);
  const maxZoom = settings.maxZoom;

  const selectedDwell =
    employees.find((e) => e.employee.id === selectedId)?.dwell ?? null;
  const replayPoint = route && replayIndex !== null ? route.points[replayIndex] : undefined;

  const center = useMemo<[number, number]>(() => {
    const selected = positioned.find((e) => e.employee.id === selectedId)?.position;
    const first = selected ?? positioned[0]?.position;
    // Bengaluru rather than (0,0): an empty map in the Gulf of Guinea reads as
    // a bug rather than as "nobody is reporting yet".
    return first ? [first.latitude, first.longitude] : [12.9716, 77.5946];
  }, [positioned, selectedId]);

  /**
   * Legs the trail must not be drawn across.
   *
   * A delivery run is defined by how the data arrived, and that has nothing to
   * do with whether the employee was observed in between — so a run can span a
   * two-hour silence. Drawing it as one continuous line would assert a journey
   * across ground nobody watched, which is exactly the claim the dashed gap
   * rendering exists to avoid making. The legs are collected once and every
   * polyline is split on them.
   */
  const gapLegs = useMemo(() => {
    const legs = new Set<number>();
    for (const segment of route?.segments ?? []) {
      if (segment.kind !== 'gap') continue;
      for (let i = segment.fromIndex; i < segment.toIndex; i++) legs.add(i);
    }
    return legs;
  }, [route]);

  const showAllDelivery = deliveryFilter.size === 0;
  const isVisible = (kind: PointDelivery) => showAllDelivery || deliveryFilter.has(kind);

  return (
    <div className={cn('relative overflow-hidden rounded-xl border border-border', className)}>
      <MapContainer
        center={center}
        zoom={13}
        // Deep zoom is the point of the setting: above the provider's own
        // maximum Leaflet upscales the last real tile rather than giving up, so
        // this buys magnification for pulling apart fixes that landed a few
        // metres from one another. maxNativeZoom on each layer is what stops it
        // requesting tiles that do not exist.
        maxZoom={maxZoom}
        className="h-full w-full"
        zoomControl={false}
      >
        <Basemap
          basemap={basemap}
          settings={settings}
          maxZoom={maxZoom}
          onGoogleError={setGoogleError}
        />
        {/*
         * Under a failed Google session the map would otherwise be blank. Falling
         * back to OSM keeps it usable while the banner below explains why it does
         * not look like what was chosen.
         */}
        {googleError ? (
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={maxZoom}
            maxNativeZoom={19}
          />
        ) : null}

        <ZoomControl position="bottomright" />
        <KeepMapSized />
        <ImageryClass imagery={definition.imagery && !googleError} />
        <FitToData route={route} fallback={center} selectedId={selectedId} maxZoom={maxZoom} />
        <FocusStop route={route} index={focusStop} maxZoom={maxZoom} />
        <FocusRange route={route} range={focusRange} maxZoom={maxZoom} />

        {/* Silences, drawn in BOTH colour modes. A gap is missing data, not a
            value on either scale, so it never takes a movement or a delivery
            colour. */}
        {route?.segments.map((segment, i) => {
          if (segment.kind !== 'gap') return null;
          const slice = route.points.slice(segment.fromIndex, segment.toIndex + 1);
          if (slice.length < 2) return null;
          return (
            <Polyline
              key={`gap-${i}`}
              positions={slice.map((p) => [p.lat, p.lng] as [number, number])}
              pathOptions={{
                color: 'hsl(var(--track-gap))',
                weight: 2,
                opacity: 0.55,
                dashArray: '4 8',
              }}
            />
          );
        })}

        {/* The trail, coloured by what the employee was doing. */}
        {colourBy === 'movement'
          ? route?.segments.map((segment, i) => {
              if (segment.kind === 'gap') return null;
              const slice = route.points.slice(segment.fromIndex, segment.toIndex + 1);
              if (slice.length < 2) return null;
              return (
                <Polyline
                  key={`seg-${i}`}
                  positions={slice.map((p) => [p.lat, p.lng] as [number, number])}
                  pathOptions={{
                    color:
                      segment.kind === 'moving'
                        ? 'hsl(var(--track-moving))'
                        : 'hsl(var(--track-idle))',
                    weight: segment.kind === 'moving' ? 4 : 3,
                    opacity: segment.kind === 'moving' ? 0.85 : 0.5,
                  }}
                />
              );
            })
          : null}

        {/* …or by when we found out about it. */}
        {colourBy === 'delivery'
          ? route?.deliveryRuns.flatMap((run, i) => {
              if (!isVisible(run.kind)) return [];
              const parts = splitAtGaps(route, run.fromIndex, run.toIndex, gapLegs);
              const color = DELIVERY_CSS[run.kind];
              const casing = DELIVERY_CASING[run.kind];

              return parts.flatMap((positions, part) => {
                const key = `dl-${i}-${part}`;
                const lines = [];
                if (casing > 0) {
                  lines.push(
                    <Polyline
                      key={`${key}-casing`}
                      positions={positions}
                      interactive={false}
                      pathOptions={{
                        color,
                        weight: casing,
                        opacity: 0.22,
                        lineCap: 'round',
                        lineJoin: 'round',
                      }}
                    />,
                  );
                }
                lines.push(
                  <Polyline
                    key={key}
                    positions={positions}
                    pathOptions={{ color, weight: 4, opacity: 0.92 }}
                  >
                    <Popup>
                      <div className="space-y-0.5 text-xs">
                        <div className="text-sm font-semibold">{DELIVERY_LABEL[run.kind]}</div>
                        <div className="tabular-nums">
                          {formatClock(run.from, route.timezone)} –{' '}
                          {formatClock(run.to, route.timezone)} · {run.pointCount} fixes
                        </div>
                        {run.kind === 'live' ? null : (
                          <div>
                            Reached the server {formatClock(run.uploadedAt, route.timezone)}, up to{' '}
                            {formatLag(run.maxLagSeconds)} late
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Polyline>,
                );
                return lines;
              });
            })
          : null}

        <TrailArrows route={route} enabled={colourBy === 'movement'} />

        {/* Stretches captured with no signal, per the DEVICE's own radio flag.
            Kept in movement mode as a casing over the trail — it is a different
            claim from the delivery colouring (which measures arrival) and the
            two genuinely disagree when a phone had bars but could not reach us.
            In delivery mode it would be a third encoding on the same line, so
            it is dropped there. */}
        {colourBy === 'movement'
          ? route?.offlineRuns.map((run, i) => {
              const slice = route.points.slice(run.fromIndex, run.toIndex + 1);
              if (slice.length < 2) return null;
              return (
                <Polyline
                  key={`offline-${i}`}
                  positions={slice.map((p) => [p.lat, p.lng] as [number, number])}
                  interactive={false}
                  pathOptions={{
                    color: 'hsl(var(--track-stopped))',
                    weight: 9,
                    opacity: 0.28,
                    lineCap: 'round',
                  }}
                />
              );
            })
          : null}

        {/* Stop radius first, so the numbered pin sits on top of its own circle. */}
        {route?.stops.map((stop) => (
          <Circle
            key={`stop-area-${stop.index}`}
            center={[stop.latitude, stop.longitude]}
            radius={Math.max(stop.radiusMeters, 25)}
            pathOptions={{
              color: 'hsl(var(--track-stopped))',
              fillColor: 'hsl(var(--track-stopped))',
              fillOpacity: 0.12,
              weight: 1,
            }}
          />
        ))}

        {route?.stops.map((stop) => (
          <Marker
            key={`stop-${stop.index}`}
            position={[stop.latitude, stop.longitude]}
            icon={stopIcon(stop.index, stop.type === 'long_stop')}
            zIndexOffset={200}
          >
            <Popup>
              <div className="space-y-0.5 text-xs">
                <div className="text-sm font-semibold">
                  Stop {stop.index + 1}
                  {stop.type === 'long_stop' ? ' · long' : ''}
                </div>
                <div>{stop.place ?? formatCoordinates(stop.latitude, stop.longitude)}</div>
                <div className="tabular-nums">
                  {formatClock(stop.startedAt, route.timezone)} –{' '}
                  {stop.isOpen ? 'still there' : formatClock(stop.endedAt, route.timezone)}
                </div>
                <div className="tabular-nums">
                  {formatDuration(stop.durationSeconds)} · {stop.pointCount} fixes · ±
                  {stop.radiusMeters} m
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Where the selected employee is standing still right now. */}
        {selectedDwell ? (
          <Circle
            center={[selectedDwell.latitude, selectedDwell.longitude]}
            radius={Math.max(selectedDwell.radiusMeters, 40)}
            pathOptions={{
              color: 'hsl(var(--track-stopped))',
              fillColor: 'hsl(var(--track-stopped))',
              fillOpacity: 0.1,
              weight: 1.5,
              dashArray: '3 4',
            }}
          />
        ) : null}

        {positioned.map((e) => {
          const p = e.position;
          if (!p) return null;
          return (
            <Marker
              key={e.employee.id}
              position={[p.latitude, p.longitude]}
              icon={employeeIcon(e, e.employee.id === selectedId)}
              zIndexOffset={e.employee.id === selectedId ? 500 : 300}
              eventHandlers={{ click: () => onSelect(e.employee.id) }}
            >
              <Popup>
                <div className="space-y-0.5 text-xs">
                  <div className="text-sm font-semibold">{e.employee.name}</div>
                  <div className="text-muted-foreground">
                    {e.employee.code}
                    {e.employee.designation ? ` · ${e.employee.designation}` : ''}
                  </div>
                  <div className="pt-1">
                    {e.movement.state === 'moving'
                      ? `Moving ${formatSpeed(e.movement.speedMps)} ${formatBearing(e.movement.bearing)}`
                      : e.dwell
                        ? `Stationary ${formatDuration(e.dwell.seconds)}`
                        : 'Stationary'}
                  </div>
                  {e.dwell ? (
                    <div>
                      at {e.dwell.place ?? formatCoordinates(e.dwell.latitude, e.dwell.longitude)}
                    </div>
                  ) : null}
                  <div className="tabular-nums">
                    {formatDistance(e.today.distanceMeters)} today · {formatAge(p.ageSeconds)}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* The replay head, drawn as a ringed dot so it reads as a playhead
            rather than as another employee. */}
        {replayPoint ? (
          <CircleMarker
            center={[replayPoint.lat, replayPoint.lng]}
            radius={8}
            pathOptions={{
              color: 'hsl(var(--background))',
              fillColor:
                colourBy === 'delivery'
                  ? DELIVERY_CSS[replayPoint.dl]
                  : 'hsl(var(--track-moving))',
              fillOpacity: 1,
              weight: 3,
            }}
          />
        ) : null}
      </MapContainer>

      <MapToolbar
        basemap={basemap}
        onBasemapChange={onBasemapChange}
        hasGoogleKey={Boolean(settings.googleMapsBrowserKey)}
        colourBy={colourBy}
        onColourByChange={onColourByChange}
        hasRoute={Boolean(route && route.points.length > 0)}
      />

      {colourBy === 'delivery' && route ? (
        <DeliveryFilter
          route={route}
          filter={deliveryFilter}
          onChange={onDeliveryFilterChange}
        />
      ) : null}

      {googleError ? (
        <div className="pointer-events-none absolute inset-x-3 top-3 z-[1000] rounded-lg border border-danger/40 bg-card/95 px-3 py-2 text-2xs text-danger shadow-sm backdrop-blur">
          Google basemap unavailable — showing OpenStreetMap. {googleError}
        </div>
      ) : null}

      <MapLegend
        colourBy={colourBy}
        hasRoute={Boolean(route && route.points.length > 0)}
        hasOffline={Boolean(route && route.offlineRuns.length > 0)}
      />
    </div>
  );
}

/**
 * Cuts a point range into drawable polylines, breaking at silences.
 *
 * Returns only the pieces with at least two points, so a run that is entirely
 * one isolated fix contributes nothing rather than an invisible zero-length
 * line Leaflet still has to lay out.
 */
function splitAtGaps(
  route: RouteDto,
  fromIndex: number,
  toIndex: number,
  gapLegs: ReadonlySet<number>,
): [number, number][][] {
  const parts: [number, number][][] = [];
  let current: [number, number][] = [];

  for (let i = fromIndex; i <= toIndex; i++) {
    const point = route.points[i];
    if (!point) continue;
    current.push([point.lat, point.lng]);
    if (gapLegs.has(i)) {
      if (current.length >= 2) parts.push(current);
      current = [];
    }
  }
  if (current.length >= 2) parts.push(current);

  return parts;
}

/** Pixels of screen-space between arrows along the trail. */
const ARROW_SPACING_PX = 90;

/** Below this the leg is too short to carry a legible arrow. */
const MIN_LEG_PX = 6;

/**
 * Pixels each arrow sits to the RIGHT of its direction of travel.
 *
 * Without it, an out-and-back — a delivery round, any there-and-return trip —
 * draws both directions on the same pixels, so opposing arrows land on top of
 * one another and whichever renders last wins. Measured on a real route, pairs
 * were ending up 17px apart and pointing opposite ways, which is worse than no
 * arrow: it looks like a single line that cannot make up its mind.
 *
 * Offsetting by the traveller's right hand puts the outbound and return runs in
 * two parallel lanes, each pointing its own way — the same convention a road
 * atlas uses for a dual carriageway, and unambiguous at a glance. It also lifts
 * the arrow clear of the 4px line so the chevron is not competing with the
 * colour underneath it.
 */
const ARROW_OFFSET_PX = 6;

/**
 * Direction-of-travel arrows along the trail.
 *
 * A polyline is symmetric: on its own it shows the route but not which way it
 * was driven, and for a day that doubles back on itself — a delivery round, a
 * there-and-return trip — that is exactly the question being asked. The live
 * marker already carries a heading, but only for right now; the historical
 * trace had no direction at all.
 *
 * Spaced by SCREEN distance rather than by point index or by metres. Point
 * index would cluster arrows wherever the device sampled fastest, which is
 * while stationary; metres would give a motorway leg three arrows and a city
 * block thirty. Even pixel spacing is what looks deliberate at every zoom, and
 * it is why this recomputes on zoom and pan rather than once.
 *
 * Only `moving` segments get them. A stop has no direction, and a gap's
 * straight line is an inference — putting an arrow on it would assert a
 * direction of travel across ground nobody was observed crossing.
 *
 * Suppressed entirely in delivery mode: the arrows are drawn in the movement
 * blue, and a blue chevron sitting on an amber line is a third colour claiming
 * to belong to the scale it is drawn over.
 */
function TrailArrows({ route, enabled }: { route: RouteDto | null; enabled: boolean }) {
  const map = useMap();
  const [arrows, setArrows] = useState<
    { lat: number; lng: number; angle: number; key: string }[]
  >([]);

  useEffect(() => {
    if (!enabled || !route || route.points.length < 2) {
      setArrows([]);
      return;
    }

    const compute = () => {
      const bounds = map.getBounds().pad(0.15);
      const next: { lat: number; lng: number; angle: number; key: string }[] = [];

      for (const segment of route.segments) {
        if (segment.kind !== 'moving') continue;

        // Carried across the whole segment so spacing does not restart at
        // every fix — otherwise a dense stretch sprouts an arrow per point.
        let sinceLast = ARROW_SPACING_PX / 2;

        for (let i = segment.fromIndex; i < segment.toIndex; i++) {
          const a = route.points[i];
          const b = route.points[i + 1];
          if (!a || !b) continue;

          const pa = map.latLngToLayerPoint([a.lat, a.lng]);
          const pb = map.latLngToLayerPoint([b.lat, b.lng]);
          const dx = pb.x - pa.x;
          const dy = pb.y - pa.y;
          const legPx = Math.hypot(dx, dy);
          if (legPx < MIN_LEG_PX) continue;

          // Screen-space angle, not a geographic bearing: the arrow has to line
          // up with the drawn line, and under Web Mercator those diverge away
          // from the equator.
          const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

          // Unit vector to the traveller's right. Screen y grows downward, so
          // for a heading of (dx, dy) that is (-dy, dx): travelling east (1, 0)
          // gives (0, 1), which is south — right of east. Getting this backwards
          // would put the outbound and return lanes on the wrong sides, which
          // still separates them but reads as the wrong side of the road.
          const rightX = (-dy / legPx) * ARROW_OFFSET_PX;
          const rightY = (dx / legPx) * ARROW_OFFSET_PX;

          let travelled = ARROW_SPACING_PX - sinceLast;
          while (travelled <= legPx) {
            const t = travelled / legPx;
            const point = map.layerPointToLatLng([
              pa.x + dx * t + rightX,
              pa.y + dy * t + rightY,
            ]);
            // Culled to the viewport: panning a long route otherwise leaves
            // hundreds of off-screen markers in the DOM.
            if (bounds.contains(point)) {
              next.push({
                lat: point.lat,
                lng: point.lng,
                angle,
                key: `${i}-${travelled.toFixed(0)}`,
              });
            }
            travelled += ARROW_SPACING_PX;
          }
          sinceLast = (sinceLast + legPx) % ARROW_SPACING_PX;
        }
      }

      setArrows(next);
    };

    compute();
    map.on('zoomend', compute);
    map.on('moveend', compute);
    return () => {
      map.off('zoomend', compute);
      map.off('moveend', compute);
    };
  }, [route, map, enabled]);

  return (
    <>
      {arrows.map((arrow) => (
        <Marker
          key={arrow.key}
          position={[arrow.lat, arrow.lng]}
          icon={arrowIcon(arrow.angle)}
          // Never interactive: these are annotation on the line, and a stack of
          // them must not swallow clicks meant for a stop pin or the map.
          interactive={false}
          keyboard={false}
          zIndexOffset={100}
        />
      ))}
    </>
  );
}

/** A chevron pointing along +x, rotated into the leg's screen direction. */
function arrowIcon(angle: number): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div style="transform:rotate(${angle.toFixed(1)}deg);width:14px;height:14px;display:grid;place-items:center">
             <svg viewBox="0 0 12 12" width="12" height="12">
               <path d="M3 1.5 L8 6 L3 10.5" fill="none"
                     stroke="hsl(var(--background))" stroke-width="4"
                     stroke-linecap="round" stroke-linejoin="round" opacity="0.8"/>
               <path d="M3 1.5 L8 6 L3 10.5" fill="none"
                     stroke="hsl(var(--track-moving))" stroke-width="2.2"
                     stroke-linecap="round" stroke-linejoin="round"/>
             </svg>
           </div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

/**
 * Tells Leaflet the container's real size, whenever it changes.
 *
 * Leaflet measures its container once at construction and caches the result. In
 * a CSS grid that is still resolving — which this is, the map sharing a row with
 * a 360px roster that only settles at the `xl` breakpoint — that measurement is
 * of a box smaller than the one the map ends up in. Leaflet then requests tiles
 * for the box it thinks it has, and the rest of the container stays empty grey
 * until something else forces a redraw. On first load that looked exactly like
 * a half-broken map; it only healed when an employee was selected, because
 * `fitBounds` happens to invalidate the same cache.
 *
 * A ResizeObserver rather than a one-shot call after mount: the same staleness
 * returns whenever the browser window is resized or the detail panel below
 * changes height.
 */
function KeepMapSized() {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    // The first correction has to wait for layout — calling it synchronously on
    // mount re-measures the same not-yet-settled box.
    const raf = requestAnimationFrame(() => map.invalidateSize({ animate: false }));

    const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    observer.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [map]);

  return null;
}

/**
 * Marks the container when the basemap is photographic.
 *
 * A class rather than an inline style, because what it switches off lives in a
 * stylesheet: dark mode inverts the tile pane, which is right for a street map
 * and ruinous for satellite imagery.
 */
function ImageryClass({ imagery }: { imagery: boolean }) {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    container.classList.toggle('basemap-imagery', imagery);
    return () => container.classList.remove('basemap-imagery');
  }, [map, imagery]);

  return null;
}

/**
 * Fits the viewport to the selected employee's day when a route arrives.
 *
 * Keyed on the route identity rather than on every render, so it frames the
 * data once and then leaves the map alone — a view that re-centres itself under
 * a hand that is panning is unusable.
 */
function FitToData({
  route,
  fallback,
  selectedId,
  maxZoom,
}: {
  route: RouteDto | null;
  fallback: [number, number];
  selectedId: number | null;
  maxZoom: number;
}) {
  const map = useMap();
  const key = route?.bounds ? `${route.employeeId}:${route.date}` : `none:${selectedId ?? ''}`;

  // Refs, so the effect can read the latest values without listing them as
  // dependencies — the whole point is that it fires on `key` alone.
  const boundsRef = useRef(route?.bounds ?? null);
  const fallbackRef = useRef(fallback);
  boundsRef.current = route?.bounds ?? null;
  fallbackRef.current = fallback;

  useEffect(() => {
    const bounds = boundsRef.current;
    if (bounds) {
      map.fitBounds(
        [
          [bounds.south, bounds.west],
          [bounds.north, bounds.east],
        ],
        // 17 rather than 16: a day spent inside one industrial estate framed at
        // 16 is a blue smudge, and the first thing anyone did was zoom in.
        { padding: [40, 40], maxZoom: Math.min(17, maxZoom) },
      );
    } else {
      map.setView(fallbackRef.current, map.getZoom());
    }
  }, [key, map]);

  return null;
}

/** Pans to a stop when one is clicked in the list, without changing zoom. */
function FocusStop({
  route,
  index,
  maxZoom,
}: {
  route: RouteDto | null;
  index: number | null;
  maxZoom: number;
}) {
  const map = useMap();

  useEffect(() => {
    if (index === null || !route) return;
    const stop = route.stops[index];
    if (!stop) return;
    map.setView([stop.latitude, stop.longitude], Math.min(Math.max(map.getZoom(), 17), maxZoom));
  }, [index, route, map, maxZoom]);

  return null;
}

/**
 * Frames a stretch of the trail — an upload episode clicked in the detail panel.
 *
 * Fits the bounds rather than centring on a midpoint: a backfilled run can span
 * a whole afternoon's driving, and centring would put half of it off screen.
 */
function FocusRange({
  route,
  range,
  maxZoom,
}: {
  route: RouteDto | null;
  range: IndexRange | null;
  maxZoom: number;
}) {
  const map = useMap();

  useEffect(() => {
    if (!range || !route) return;
    const slice = route.points.slice(range.fromIndex, range.toIndex + 1);
    const only = slice[0];
    if (!only) return;

    // One fix cannot be fitted to — Leaflet resolves a zero-area bounds to its
    // maximum zoom, which slams the view to street level on a single point.
    if (slice.length === 1) {
      map.setView([only.lat, only.lng], Math.min(18, maxZoom));
      return;
    }

    map.fitBounds(
      slice.map((p) => [p.lat, p.lng] as [number, number]),
      { padding: [60, 60], maxZoom: Math.min(17, maxZoom) },
    );
  }, [range, route, map, maxZoom]);

  return null;
}

/** Basemap picker and colour-mode switch, stacked in the top-right corner. */
function MapToolbar({
  basemap,
  onBasemapChange,
  hasGoogleKey,
  colourBy,
  onColourByChange,
  hasRoute,
}: {
  basemap: BasemapId;
  onBasemapChange: (id: BasemapId) => void;
  hasGoogleKey: boolean;
  colourBy: ColourBy;
  onColourByChange: (mode: ColourBy) => void;
  hasRoute: boolean;
}) {
  const options = availableBasemaps(hasGoogleKey);

  return (
    <div className="absolute right-3 top-3 z-[1000] flex flex-col items-end gap-2">
      <select
        value={basemap}
        onChange={(e) => onBasemapChange(e.target.value as BasemapId)}
        aria-label="Basemap"
        className="rounded-lg border border-border bg-card/95 px-2 py-1.5 text-2xs shadow-sm backdrop-blur"
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>

      {!hasGoogleKey ? (
        <span className="max-w-[13rem] rounded-lg border border-border bg-card/95 px-2 py-1 text-right text-2xs text-muted-foreground shadow-sm backdrop-blur">
          Add a Google Maps key in Settings for hybrid imagery and place names.
        </span>
      ) : null}

      {hasRoute ? (
        <div className="flex overflow-hidden rounded-lg border border-border bg-card/95 text-2xs shadow-sm backdrop-blur">
          {(['movement', 'delivery'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => onColourByChange(mode)}
              className={cn(
                'px-2 py-1.5 transition-colors',
                colourBy === mode
                  ? 'bg-muted font-medium text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {mode === 'movement' ? 'Movement' : 'Data delivery'}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Which delivery classes are drawn.
 *
 * Counts on the chips, because the useful question is usually "was ANY of this
 * backfilled?" and a chip reading `Backfilled 0` answers it without a click.
 * A class with no points is disabled rather than hidden, so the control does
 * not change shape between employees.
 */
function DeliveryFilter({
  route,
  filter,
  onChange,
}: {
  route: RouteDto;
  filter: ReadonlySet<PointDelivery>;
  onChange: (next: ReadonlySet<PointDelivery>) => void;
}) {
  const counts: Record<PointDelivery, number> = {
    live: route.summary.livePointCount,
    delayed: route.summary.delayedPointCount,
    backfill: route.summary.backfillPointCount,
  };

  const toggle = (kind: PointDelivery) => {
    const next = new Set(filter);
    // An empty set means "everything", so the first click has to start from the
    // full set and remove — otherwise clicking "Live" on an unfiltered map
    // would hide live points, which is the opposite of what it says.
    if (next.size === 0) {
      next.add('live');
      next.add('delayed');
      next.add('backfill');
    }
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    // Back to "everything" rather than to a blank map.
    if (next.size === 3 || next.size === 0) onChange(new Set());
    else onChange(next);
  };

  const showAll = filter.size === 0;

  return (
    <div className="absolute left-3 top-3 z-[1000] flex flex-wrap gap-1.5">
      {(['live', 'delayed', 'backfill'] as const).map((kind) => {
        const active = showAll || filter.has(kind);
        const count = counts[kind];
        return (
          <button
            key={kind}
            type="button"
            disabled={count === 0}
            onClick={() => toggle(kind)}
            aria-pressed={active}
            className={cn(
              'flex items-center gap-1.5 rounded-lg border px-2 py-1 text-2xs shadow-sm backdrop-blur transition-opacity',
              'border-border bg-card/95',
              active ? 'opacity-100' : 'opacity-45',
              count === 0 ? 'cursor-not-allowed opacity-35' : 'hover:opacity-100',
            )}
          >
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: DELIVERY_CSS[kind] }}
            />
            {DELIVERY_LABEL[kind]}
            <span className="tabular-nums text-muted-foreground">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

function MapLegend({
  colourBy,
  hasRoute,
  hasOffline,
}: {
  colourBy: ColourBy;
  hasRoute: boolean;
  hasOffline: boolean;
}) {
  return (
    <div className="pointer-events-none absolute bottom-3 left-3 z-[1000] rounded-lg border border-border bg-card/90 px-2.5 py-2 text-2xs shadow-sm backdrop-blur">
      <ul className="space-y-1">
        {colourBy === 'delivery' ? (
          <>
            <LegendRow color={DELIVERY_CSS.live} label="On the map as it happened" />
            <LegendRow color={DELIVERY_CSS.delayed} label="Arrived late" />
            <LegendRow color={DELIVERY_CSS.backfill} label="Uploaded in bulk after reconnecting" />
          </>
        ) : (
          <>
            <LegendRow color="hsl(var(--track-moving))" label="Moving (arrow shows course)" />
            <LegendRow color="hsl(var(--track-stopped))" label="Stationary" />
            <LegendRow color="hsl(var(--warning))" label="Position stale" />
            <LegendRow color="hsl(var(--danger))" label="Mock location" />
          </>
        )}
        {hasRoute && colourBy === 'movement' ? (
          <li className="flex items-center gap-1.5 text-muted-foreground">
            <span className="inline-flex w-4 items-center justify-center text-track-moving">
              <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden>
                <path
                  d="M3 1.5 L8 6 L3 10.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            Direction travelled
          </li>
        ) : null}
        {hasRoute ? (
          <li className="flex items-center gap-1.5 text-muted-foreground">
            <span className="inline-block h-0 w-4 border-t-2 border-dashed border-track-gap" />
            No data
          </li>
        ) : null}
        {hasOffline && colourBy === 'movement' ? (
          <li className="flex items-center gap-1.5 text-muted-foreground">
            <span className="inline-block h-2 w-4 rounded-full bg-track-stopped/30" />
            Captured with no signal
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function LegendRow({ color, label }: { color: string; label: string }) {
  return (
    <li className="flex items-center gap-1.5 text-muted-foreground">
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ background: color }}
      />
      {label}
    </li>
  );
}
